var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import apiClient from '@utils/api-client';
import { BULK_UPDATE_ACTION_REPORT_OPTIONS } from '../constants';
const basePath = (workOrderId) => `/api/internal/work_orders/${workOrderId}/attachments`;
/**
 * Fetches every support document for a work order in one request — the union of uploaded
 * attachments, work order reports, inspection documents and proposal documents.
 *
 * The response is rooted at `attachments` rather than being a bare array, because this surface's
 * rows are not all attachments and a bare array gave the reader no place to say so. Not paginated;
 * if it ever becomes so, this call must opt out explicitly rather than silently rendering a
 * truncated page.
 */
export function fetchWorkOrderAttachments(workOrderId) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield apiClient.get(basePath(workOrderId));
        return response.data.attachments;
    });
}
/**
 * Single-field partial payload, returning the server-confirmed row. `rowId` is the namespaced id
 * (`'attachment:12'`); the controller refuses anything that is not an attachment, because a
 * generated document's name belongs to the Report it points at and writing it here would be
 * silently discarded.
 */
export function updateWorkOrderAttachment(workOrderId, rowId, payload) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield apiClient.patch(`${basePath(workOrderId)}/${rowId}`, {
            workOrderAttachment: payload
        });
        return response.data.attachment;
    });
}
/**
 * Mass "Update report options". Only the keys supplied are written. Linked-source ids are dropped
 * server-side rather than trusted to never arrive.
 */
export function bulkUpdateAttachmentReportOptions(workOrderId, ids, options) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield apiClient.patch(`${basePath(workOrderId)}/bulk_update`, Object.assign({ ids, updateAction: BULK_UPDATE_ACTION_REPORT_OPTIONS }, options));
        return response.data;
    });
}
/**
 * Mass destroy. Always sends explicit ids: this endpoint treats absent AND empty as "no rows", so
 * a cleared selection can never delete every attachment on the work order.
 */
export function bulkDestroyAttachments(workOrderId, ids) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield apiClient.delete(`${basePath(workOrderId)}/bulk_destroy`, {
            data: { ids }
        });
        return response.data;
    });
}
/**
 * Single-row removal. The same endpoint serves both kinds — the controller dispatches on the id's
 * source, destroying an uploaded attachment (and its S3 object) or unlinking a generated one.
 */
export function destroyAttachment(workOrderId, rowId) {
    return __awaiter(this, void 0, void 0, function* () {
        yield apiClient.delete(`${basePath(workOrderId)}/${rowId}`);
    });
}
